import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import { getTodosInDateRange, getTodos } from '../services/todo/storage';
import { getHolidaysInRange, type Holiday } from '../services/todo/holidays';
import { toggleTodoComplete, getBaseTodoId } from '../services/todo/storage';
import { getRecurrenceTypeLabel } from '../services/todo/recurrence';
import { useAuth } from '../hooks/useAuth';

type QuickDateOption = 'today' | 'tomorrow' | 'nextWeek' | 'noDate';

interface CalendarListViewProps {
  onTodoClick?: (todo: Todo) => void;
  onDateClick?: (date: Date) => void;
  onDateDoubleClick?: (date: Date) => void;
  /** 快捷添加：输入标题和 startDate（0=无日期）即创建待办 */
  onQuickAdd?: (title: string, startDate: number) => void | Promise<void>;
  selectedDate?: Date | null;
  isMobile?: boolean;
  selectedGroupIds?: Set<string>; // 选中的分组ID集合（支持多选）
  /** 已登录时传入当前数据源，勾选时传给 toggleTodoComplete 避免依赖未同步的 localStorage */
  todosForToggle?: Todo[] | null;
  /** 已登录时传入：切换时实时获取最新 todos，避免闭包滞后 */
  getCurrentTodosForToggle?: () => Todo[] | undefined;
  /** 勾选成功后传入更新后的 todo，用于局部更新 React Query 缓存 */
  onTodoUpdated?: (updatedTodo: Todo) => void;
  /** 移动端：返回书签回调，传入时在顶部左侧显示返回按钮 */
  onBack?: () => void;
}

/** 无日期占位值，仅用于待办视图展示，不参与日历格 */
const NO_DATE_PLACEHOLDER = 0;

interface DateGroup {
  date: Date;
  dateStr: string;
  holiday?: Holiday;
  todos: Todo[];
  /** 是否为「无日期」分组（待办视图中 startDate 为 0 的项） */
  isNoDateGroup?: boolean;
}

function getStartDateForQuickOption(option: QuickDateOption): number {
  if (option === 'noDate') return NO_DATE_PLACEHOLDER;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (option === 'today') return d.getTime();
  if (option === 'tomorrow') {
    d.setDate(d.getDate() + 1);
    return d.getTime();
  }
  // nextWeek: 下周一（或产品约定「下周」）
  d.setDate(d.getDate() + (8 - d.getDay()) % 7 || 7);
  return d.getTime();
}

const CalendarListView: React.FC<CalendarListViewProps> = ({
  onTodoClick,
  onDateClick,
  onDateDoubleClick,
  onQuickAdd,
  selectedDate: externalSelectedDate,
  isMobile = false,
  selectedGroupIds = new Set<string>(),
  todosForToggle,
  getCurrentTodosForToggle,
  onTodoUpdated,
  onBack,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | null>(null);
  const [dateGroups, setDateGroups] = useState<DateGroup[]>([]);
  const [holidays, setHolidays] = useState<Map<string, Holiday>>(new Map());
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickDateOption, setQuickDateOption] = useState<QuickDateOption>('today');
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef<boolean>(false);
  const currentLongPressDateRef = useRef<Date | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPositionRef = useRef<{ x: number; y: number } | null>(null);

  // 使用外部传入的 selectedDate，如果没有则使用内部的
  const selectedDate = externalSelectedDate !== undefined ? externalSelectedDate : internalSelectedDate;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月',
  ];

  // 获取当前用户ID（使用 useAuth hook）
  const { user } = useAuth();
  const currentUserId = user?.id || null;

  // 加载当前月份的待办事项和节假日
  useEffect(() => {
    loadMonthData();
  }, [currentDate, selectedGroupIds, currentUserId]);

  const loadMonthData = async () => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    endDate.setHours(23, 59, 59, 999);

    // 先获取该日期范围内的所有待办事项（包括重复事项的展开；无日期项已由 getTodosInDateRangeFromList 排除）
    let todosList = await getTodosInDateRange(startDate, endDate);

    // 应用分组筛选（支持多选）；未选分组(selectedGroupIds.size===0) 视为「不筛选」，显示全部
    const filterByGroup = (todo: Todo) => {
      if (!selectedGroupIds || selectedGroupIds.size === 0) return true;
      if (!todo.groupId) return false;
      return selectedGroupIds.has(todo.groupId);
    };
    if (selectedGroupIds && selectedGroupIds.size > 0) {
      todosList = todosList.filter(filterByGroup);
    }

    // 待办视图中包含无日期项：单独成组「无日期」
    const allTodos = await getTodos();
    const noDateTodos = allTodos.filter(
      (t) => t.startDate === NO_DATE_PLACEHOLDER && filterByGroup(t)
    );

    // 异步加载节假日（不阻塞UI渲染）
    // 先清空节假日数据，避免显示旧数据
    setHolidays(new Map());
    
    // 使用 requestIdleCallback 或 setTimeout 延迟加载，优先渲染UI
    const loadHolidaysAsync = async () => {
      try {
        const holidaysList = await getHolidaysInRange(startDate, endDate);
        const holidaysMap = new Map<string, Holiday>();
        holidaysList.forEach(holiday => {
          holidaysMap.set(holiday.date, holiday);
        });
        setHolidays(holidaysMap);
      } catch (error) {
        console.error('Failed to load holidays:', error);
      }
    };
    
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        loadHolidaysAsync();
      }, { timeout: 1000 });
    } else {
      setTimeout(() => {
        loadHolidaysAsync();
      }, 100);
    }

    // 生成该月的所有日期
    const daysInMonth = endDate.getDate();
    const groups: DateGroup[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dateKey = date.toDateString();

      // 获取该日期的待办事项
      const dayTodos = todosList.filter(todo => {
        const todoDate = new Date(todo.startDate);
        return todoDate.toDateString() === dateKey;
      });

      // 获取节假日信息
      const holiday = holidays.get(dateStr);

      groups.push({
        date,
        dateStr,
        holiday,
        todos: dayTodos,
      });
    }

    // 无日期待办单独成组，排在当月日期之后
    if (noDateTodos.length > 0) {
      groups.push({
        date: new Date(0), // 占位，仅用于类型
        dateStr: '无日期',
        todos: noDateTodos,
        isNoDateGroup: true,
      });
    }

    setDateGroups(groups);
  };

  // 选择日期（单击）
  const handleDateClick = async (date: Date) => {
    if (externalSelectedDate === undefined) {
      setInternalSelectedDate(date);
    }
    onDateClick?.(date);
  };

  // 双击日期处理
  const handleDateDoubleClick = (date: Date) => {
    onDateDoubleClick?.(date);
  };

  // 移动端长按处理（检测滚动状态）
  const handleLongPressStart = useCallback((date: Date) => {
    if (!isMobile) return;
    
    // 如果正在滚动，取消长按
    if (isScrollingRef.current) {
      return;
    }

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    longPressTriggeredRef.current = false;
    currentLongPressDateRef.current = date;

    longPressTimerRef.current = setTimeout(() => {
      // 再次检查是否在滚动
      if (!isScrollingRef.current) {
        longPressTriggeredRef.current = true;
        // 长按：选中日期并打开新增待办事项表单
        handleDateDoubleClick(date);
      }
      currentLongPressDateRef.current = null;
    }, 500);
  }, [isMobile]);

  const handleLongPressEnd = useCallback((_date: Date, onClick: () => void) => {
    const wasLongPress = longPressTriggeredRef.current;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    longPressTriggeredRef.current = false;
    currentLongPressDateRef.current = null;

    if (!wasLongPress) {
      onClick();
    }
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // 切换到上一个月
  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  // 切换到下一个月
  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // 跳转到今天
  const goToToday = () => {
    setCurrentDate(new Date());
    const today = new Date();
    if (externalSelectedDate === undefined) {
      setInternalSelectedDate(today);
    }
    onDateClick?.(today);
  };

  // 判断是否为今天
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // 判断是否选中
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    );
  };

  // 检测滚动状态
  useEffect(() => {
    if (!isMobile) return;
    
    const container = document.querySelector('.calendar-list-view-root');
    if (!container) return;
    
    const handleScroll = () => {
      isScrollingRef.current = true;
      
      // 清除之前的定时器
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // 取消长按定时器
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        longPressTriggeredRef.current = false;
        currentLongPressDateRef.current = null;
      }
      
      // 滚动停止后重置状态
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchmove', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchmove', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isMobile]);

  // 移动端长按处理函数（检测触摸移动）
  const handleMobileTouchStart = isMobile ? (e: React.TouchEvent | React.MouseEvent, date: Date) => {
    const touchEvent = e as React.TouchEvent;
    if (touchEvent.touches && touchEvent.touches.length > 0) {
      touchStartPositionRef.current = {
        x: touchEvent.touches[0].clientX,
        y: touchEvent.touches[0].clientY,
      };
    }
    handleLongPressStart(date);
  } : undefined;

  const handleMobileTouchMove = isMobile ? (e: React.TouchEvent) => {
    if (!touchStartPositionRef.current) return;
    
    const touchEvent = e as React.TouchEvent;
    if (touchEvent.touches && touchEvent.touches.length > 0) {
      const deltaX = Math.abs(touchEvent.touches[0].clientX - touchStartPositionRef.current.x);
      const deltaY = Math.abs(touchEvent.touches[0].clientY - touchStartPositionRef.current.y);
      
      // 如果移动距离超过10px，认为是滚动，取消长按
      if (deltaX > 10 || deltaY > 10) {
        isScrollingRef.current = true;
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          longPressTriggeredRef.current = false;
          currentLongPressDateRef.current = null;
        }
      }
    }
  } : undefined;

  const handleMobileTouchEnd = isMobile ? (e: React.TouchEvent | React.MouseEvent, date: Date) => {
    touchStartPositionRef.current = null;
    
    // 如果正在滚动，不处理点击事件
    if (isScrollingRef.current) {
      isScrollingRef.current = false;
      return;
    }
    
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
      handleLongPressEnd(date, () => {});
      return;
    }
    if (target.closest('input[type="checkbox"]')) {
      handleLongPressEnd(date, () => {});
      return;
    }
    if (target.closest('[data-todo-item]')) {
      handleLongPressEnd(date, () => {});
      return;
    }
    handleLongPressEnd(date, () => handleDateClick(date));
  } : undefined;

  const submitQuickAdd = useCallback(() => {
    const t = quickAddTitle.trim();
    if (!t || !onQuickAdd) return;
    onQuickAdd(t, getStartDateForQuickOption(quickDateOption));
    setQuickAddTitle('');
  }, [quickAddTitle, quickDateOption, onQuickAdd]);

  return (
    <div className="calendar-list-view-root flex flex-col overflow-visible pb-6 h-full min-h-0">
      {/* 快捷添加（仅当父组件传入 onQuickAdd 时显示） */}
      {onQuickAdd && (
        <div className="flex-shrink-0 mb-4 space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={quickAddTitle}
              onChange={(e) => setQuickAddTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitQuickAdd(); }}
              placeholder="添加待办，回车创建"
              className="flex-1 ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 cursor-text"
            />
            <button
              type="button"
              onClick={submitQuickAdd}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              添加
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['today', 'tomorrow', 'nextWeek', 'noDate'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setQuickDateOption(opt)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer ${
                  quickDateOption === opt
                    ? 'bg-primary/20 text-primary'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {opt === 'today' ? '今天' : opt === 'tomorrow' ? '明天' : opt === 'nextWeek' ? '下周' : '无日期'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 列表头部 */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          {isMobile && onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-xl hover:bg-primary/10 transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
              title="返回书签"
              aria-label="返回书签"
            >
              <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-ios hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        </div>

        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {year}年{monthNames[month]}
          </h2>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/80 rounded-xl transition-colors duration-200 cursor-pointer"
          >
            今天
          </button>
        </div>

        <button
          onClick={goToNextMonth}
          className="p-2 rounded-ios hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 日期列表：四边留白避免选中 ring 被裁切 */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 p-3">
        {dateGroups.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">加载中...</p>
          </div>
        ) : (
          dateGroups.map((group) => {
            const { date, holiday, todos, isNoDateGroup } = group;
            const isTodayDay = !isNoDateGroup && isToday(date);
            const isSelectedDay = !isNoDateGroup && isSelected(date);
            const weekdayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
            const weekday = weekdayNames[date.getDay()];

            return (
              <div
                key={isNoDateGroup ? 'no-date' : group.dateStr}
                className={`
                  rounded-xl p-4 transition-all duration-400 ease-out cursor-pointer glass
                  ${isSelectedDay
                    ? 'ring-2 ring-primary bg-primary/10 border-primary/30'
                    : isTodayDay && !isSelectedDay
                    ? 'bg-primary/5 dark:bg-primary/10 border-border/50'
                    : 'border-border/50'
                  }
                  hover:bg-white/90 dark:hover:bg-gray-700/50
                `}
                onTouchStart={isMobile ? (e: React.TouchEvent) => {
                  handleMobileTouchStart?.(e, date);
                } : undefined}
                onTouchMove={isMobile ? (e: React.TouchEvent) => {
                  handleMobileTouchMove?.(e);
                } : undefined}
                onTouchEnd={isMobile ? (e: React.TouchEvent) => {
                  handleMobileTouchEnd?.(e, date);
                } : undefined}
                onMouseDown={isMobile ? (_e: React.MouseEvent) => {
                  handleLongPressStart(date);
                } : undefined}
                onMouseUp={isMobile ? (e: React.MouseEvent) => {
                  handleMobileTouchEnd?.(e, date);
                } : undefined}
                onClick={isMobile ? undefined : (e) => {
                  const target = e.target as HTMLElement;
                  if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
                    return;
                  }
                  if (target.closest('input[type="checkbox"]')) {
                    return;
                  }
                  if (target.closest('[data-todo-item]')) {
                    return;
                  }
                  handleDateClick(date);
                }}
                onDoubleClick={isMobile ? undefined : (e) => {
                  const target = e.target as HTMLElement;
                  if (
                    target.closest('input[type="checkbox"]') ||
                    target.closest('[data-todo-item]')
                  ) {
                    return;
                  }
                  handleDateDoubleClick(date);
                }}
                title={isMobile ? "单击选中日期，长按添加待办事项" : "单击选中日期，双击添加/编辑待办事项"}
              >
                {/* 日期头部（无日期分组显示「无日期」） */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`
                      text-xl font-semibold
                      ${isNoDateGroup ? 'text-gray-600 dark:text-gray-400' : isTodayDay ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'}
                    `}>
                      {isNoDateGroup ? '无日期' : date.getDate()}
                    </div>
                    {!isNoDateGroup && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {weekday}
                    </div>
                    )}
                    {!isNoDateGroup && holiday && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                          holiday.isWorkday && holiday.isOffDay
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                            : holiday.isWorkday && !holiday.isOffDay
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        }`}
                        title={holiday.isWorkday && holiday.isOffDay ? `${holiday.name}（调休休息）` : holiday.isWorkday && !holiday.isOffDay ? `${holiday.name}（调休补班）` : holiday.name}
                      >
                        {holiday.isWorkday && !holiday.isOffDay ? '补班' : holiday.name}
                      </span>
                    )}
                  </div>
                </div>

                {/* 待办事项列表 */}
                {todos.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 px-2 rounded-xl bg-gray-50/50 dark:bg-gray-800/30">
                    无待办事项
                  </div>
                ) : (
                  <div className="space-y-2">
                    {todos.map((todo) => {
                      const originalTodoId = getBaseTodoId(todo);
                      const isRecurrenceInstance = !!todo.recurrence && todo.id !== originalTodoId;
                      const instanceDate = isRecurrenceInstance ? todo.startDate : undefined;
                      const recurrenceLabel = todo.recurrence
                        ? getRecurrenceTypeLabel(todo.recurrence.type, todo.recurrence)
                        : null;
                      const reminderText = todo.reminder?.enabled && todo.reminder.minutesBefore?.length
                        ? todo.reminder.minutesBefore.map((m: number) => {
                            const abs = Math.abs(m);
                            if (abs >= 1440) return `提前${Math.floor(abs / 1440)}天`;
                            if (abs >= 60) return `提前${Math.floor(abs / 60)}小时`;
                            return `提前${abs}分钟`;
                          }).join('、')
                        : null;
                      const hasDate = todo.startDate !== NO_DATE_PLACEHOLDER;
                      const dateLabel = hasDate
                        ? new Date(todo.startDate).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', weekday: 'short' })
                        : '无日期';

                      return (
                        <div
                          key={todo.id}
                          data-todo-item
                          className="flex items-start gap-3 rounded-xl px-3 py-2.5 -mx-1 transition-colors duration-200 motion-reduce:transition-none hover:bg-gray-100/90 dark:hover:bg-gray-700/60 cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-gray-600/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:ring-offset-2 focus-within:ring-offset-background"
                        >
                          <input
                            type="checkbox"
                            checked={todo.completed}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newCompleted = !todo.completed;
                              const updatedGroups = dateGroups.map(group => {
                                if (group.date.toDateString() === new Date(todo.startDate).toDateString()) {
                                  return {
                                    ...group,
                                    todos: group.todos.map(t =>
                                      t.id === todo.id ? { ...t, completed: newCompleted } : t
                                    )
                                  };
                                }
                                return group;
                              });
                              setDateGroups(updatedGroups);
                              (async () => {
                                try {
                                  const updated = await toggleTodoComplete(originalTodoId, instanceDate, {
                                    getCurrentTodos: getCurrentTodosForToggle,
                                    currentTodos: getCurrentTodosForToggle ? undefined : (todosForToggle ?? undefined),
                                    currentInstanceCompleted: todo.completed,
                                  });
                                  if (updated) {
                                    const normalizedInstance = instanceDate !== undefined
                                      ? new Date(instanceDate).setHours(0, 0, 0, 0)
                                      : undefined;
                                    const completed = updated.recurrence && normalizedInstance !== undefined
                                      ? (updated.recurrence.completedDates || []).includes(normalizedInstance)
                                      : !!updated.completed;
                                    setDateGroups(prev => prev.map(g => ({
                                      ...g,
                                      todos: g.todos.map(t => t.id !== todo.id ? t : {
                                        ...t,
                                        completed,
                                        recurrence: updated.recurrence ?? t.recurrence,
                                      }),
                                    })));
                                    onTodoUpdated?.(updated);
                                  }
                                } catch (error) {
                                  console.error('[CalendarListView] 切换待办事项状态失败:', error);
                                  await loadMonthData();
                                }
                              })();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="ios-checkbox mt-1 cursor-pointer shrink-0"
                          />
                          <div
                            className="flex-1 min-w-0"
                            onDoubleClick={isMobile ? undefined : (e) => {
                              e.stopPropagation();
                              onTodoClick?.(todo);
                            }}
                            onClick={isMobile ? (e) => {
                              e.stopPropagation();
                              onTodoClick?.(todo);
                            } : undefined}
                            title={isMobile ? '单击编辑待办事项' : '双击编辑待办事项'}
                          >
                            <div
                              className={`text-sm font-medium leading-snug ${todo.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
                              style={{ color: todo.fontColor && !todo.completed ? todo.fontColor : undefined }}
                            >
                              {todo.title}
                            </div>
                            {todo.description ? (
                              <div className={`text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 ${todo.completed ? 'line-through' : ''}`}>
                                {todo.description}
                              </div>
                            ) : null}
                            {(recurrenceLabel || reminderText || true) && (
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <span className="shrink-0">{dateLabel}</span>
                                {recurrenceLabel ? (
                                  <span className="shrink-0 flex items-center gap-0.5">
                                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {recurrenceLabel}
                                  </span>
                                ) : null}
                                {reminderText ? (
                                  <span className="shrink-0 flex items-center gap-0.5">
                                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {reminderText}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CalendarListView;
